<script setup lang="ts">
import { QDialog, QMenu } from 'quasar'
import { type Ref, getCurrentInstance, onMounted, ref } from 'vue'
import { Color } from '@previo/codebooks'
import { useHandleScrollQMenu } from '../../composables/menu/handleScrollQMenu'
import { menuOffset } from '../fields/composable/menuOffset'
import PCard from '../card/PCard.vue'
import { usePrevioDS } from '../../composables/helpers'
import { PCardRound, PCardSize } from '../card/types'
import { PMaxLines } from '../typography/types'
import { PSpaceSize } from '../space/types'
import PSpace from '../space/PSpace.vue'
import PTextTitle1 from '../typography/title/PTextTitle1.vue'
import PBtn from '../btn/PBtn.vue'
import { PBtnType } from '../btn/types'
import type { UseHandleScroll } from '../../composables/menu/types/UseHandleScroll'
import { type PContextMenuPosition, PContextMenuPositionEnum } from './types'
import { useRegisterInstance } from './composables/useRegisterInstance'

type Props = {
  title: string
  cy: string
  matchParentWidth?: boolean
  persistent?: boolean
  anchor?: PContextMenuPosition
  self?: PContextMenuPosition
  scrollTarget?: HTMLElement
}

type Emits = {
  (e: 'open'): void
  (e: 'close'): void
}

const isOpen = defineModel<boolean>()

const emits = defineEmits<Emits>()

const props = withDefaults(defineProps<Props>(), {
  matchParentWidth: false,
  persistent: false,
  anchor: PContextMenuPositionEnum.BOTTOM_END,
  self: PContextMenuPositionEnum.TOP_END,
})

defineOptions({
  inheritAttrs: false,
})

const ds = usePrevioDS()

const instance = getCurrentInstance()
const qMenuComponent = ref<InstanceType<typeof QMenu>>()
const showAtCursor = ref<boolean>(false)
const handleScrollQMenu = ref<UseHandleScroll | null>(null)

const open = (): void => {
  isOpen.value = true
}

const close = (): void => {
  showAtCursor.value = false
  isOpen.value = false
  emits('close')
}

const toggle = (): void => {
  isOpen.value = !isOpen.value
}

const openAtCursor = (): void => {
  if (!instance) {
    return
  }

  useRegisterInstance().unregisterExcept(instance)

  showAtCursor.value = true
  open()
}

const onDialogHide = (): void => {
  isOpen.value = false

  emits('close')
}

const onMenuShow = (): void => {
  handleScrollQMenu.value?.subscribe()
  emits('open')
}

const onDialogShow = (): void => {
  emits('open')
}

const onMenuHide = (): void => {
  showAtCursor.value = false
  handleScrollQMenu.value?.unsubscribe()
  emits('close')
}

onMounted(() => {
  if (!instance) {
    return
  }

  useRegisterInstance().register(instance)

  handleScrollQMenu.value = useHandleScrollQMenu(
    qMenuComponent as Ref<QMenu>,
    props.scrollTarget || (instance.parent?.vnode.el as HTMLElement),
  )
})

defineExpose({
  open,
  openAtCursor,
  close,
  toggle,
})
</script>

<template>
  <q-menu
    v-if="ds.screen().isLaptopAndLarge"
    ref="qMenuComponent"
    v-model="isOpen"
    v-bind="$attrs"
    :data-cy="cy"
    :fit="matchParentWidth"
    :offset="menuOffset"
    :persistent="persistent"
    :anchor="anchor"
    :self="self"
    :no-parent-event="!showAtCursor"
    :no-refocus="!showAtCursor"
    :context-menu="showAtCursor"
    transition-duration="100"
    @show="onMenuShow"
    @hide="onMenuHide"
  >
    <p-card :pa-mobile="PCardSize.SM">
      <slot />
    </p-card>
  </q-menu>

  <q-dialog
    v-else
    v-model="isOpen"
    position="bottom"
    :transition-duration="200"
    transition-show="slide-up"
    transition-hide="slide-down"
    :persistent="persistent"
    maximized
    @show="onDialogShow"
    @hide="onDialogHide"
  >
    <p-card
      class="ds-context-menu-sheet p-display-flex p-flex-direction-column"
      :pa-mobile="PCardSize.SM"
      :pa-laptop="PCardSize.MD"
      :color="Color.WHITE"
      :round="PCardRound.ZERO"
      :round-top-start="PCardRound.LG"
      :round-top-end="PCardRound.LG"
      :data-cy="cy"
    >
      <div class="p-display-flex p-flex-center p-no-wrap">
        <p-text-title1 :color="Color.GREY_600" :text="title" :max-lines="PMaxLines.ONE" />
        <p-space :mobile="PSpaceSize.GROW" />
        <p-space :mobile="PSpaceSize.LG" />
        <p-btn :type="PBtnType.OUTLINE" icon-end="xmark-large" cy="close" @click="close()" />
      </div>
      <p-space :mobile="PSpaceSize.SM" vertical />

      <div class="ds-context-menu-sheet-content p-scroll-y">
        <slot />
      </div>
    </p-card>
  </q-dialog>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/mixins';
@import '../../assets/scss/shared/variables/ds.spacing';

.ds-context-menu-sheet {
  // Quasar sizes the maximized card to 100vh, which exceeds the layout viewport in iOS PWA
  max-height: 100dvh;
  @include safe-area-padding(left, $space-sm-mobile);
  @include safe-area-padding(right, $space-sm-mobile);
  @include safe-area-padding(bottom, $space-sm-mobile);
}

.ds-context-menu-sheet-content {
  flex: 1 1 auto;
  min-height: 0;
}
</style>
